import { useQuery } from '@tanstack/react-query'
import { Link, useParams } from 'react-router-dom'
import { QueryState } from '../components/QueryState'
import { api } from '../lib/api'
import { makeLabeler, makeRegionLabeler, useCommonCodes, useRegions, useTargetCodes } from '../lib/codes'

/**
 * 기업카드 상세.
 * 적합도·매칭 결과는 아직 보여주지 않는다 — 배점이 확정 전이다(확정요청서 B-1).
 * "확인 필요"(정보 없음)는 빈칸으로 두지 않고 그대로 표기한다(설계서 §4-4).
 */
export function SolutionDetailPage() {
  const { id } = useParams<{ id: string }>()
  const card = useQuery({
    queryKey: ['solution-card', id],
    queryFn: () => api.solutionCard(id ?? ''),
    enabled: Boolean(id),
  })

  const categoryLabel = makeLabeler(useCommonCodes('category').data)
  const targetLabel = makeLabeler(useTargetCodes().data)
  const resourceLabel = makeLabeler(useCommonCodes('resource').data)
  const areaLabel = makeLabeler(useCommonCodes('service_area').data)
  const costLabel = makeLabeler(useCommonCodes('cost_type').data)
  const pilotLabel = makeLabeler(useCommonCodes('pilot_available').data)
  const regionLabel = makeRegionLabeler(useRegions().data)

  const c = card.data

  return (
    <>
      <Link className="back-link" to="/solutions">
        ← 기업해결카드 목록
      </Link>
      <QueryState isPending={card.isPending} error={card.error}>
        {c ? (
          <article className="panel">
            <div className="detail-head">
              <div className="card-org">
                <Link to={`/orgs/${c.org.id}`}>{c.org.name}</Link>
              </div>
              <h1 className="page-title">{c.title}</h1>
              <p className="muted" style={{ margin: 0 }}>
                {c.summary}
              </p>
            </div>

            <h2 className="section-title" style={{ marginTop: 0 }}>
              이런 문제를 해결합니다
            </h2>
            <p style={{ whiteSpace: 'pre-wrap' }}>{c.problemSolved}</p>

            <h2 className="section-title">서비스 내용</h2>
            <p style={{ whiteSpace: 'pre-wrap' }}>{c.body}</p>

            <h2 className="section-title">조건</h2>
            <dl className="kv">
              <dt>분야</dt>
              <dd>{c.categoryCodes.map(categoryLabel).join(' · ')}</dd>

              <dt>대상</dt>
              <dd>{c.targetCodes.map(targetLabel).join(' · ')}</dd>

              <dt>제공 자원</dt>
              <dd>{c.resourceCodes.map(resourceLabel).join(' · ')}</dd>

              <dt>서비스 지역</dt>
              <dd>
                {c.serviceAreaCodes.map(areaLabel).join(' · ')}
                {c.serviceDongCodes && c.serviceDongCodes.length > 0
                  ? ` (${c.serviceDongCodes.map(regionLabel).join(' · ')})`
                  : ''}
              </dd>

              <dt>비용</dt>
              <dd>{costLabel(c.costType)}</dd>

              <dt>이용 조건</dt>
              <dd>{c.accessCondition ?? <span className="muted">확인 필요</span>}</dd>

              <dt>실증 가능 여부</dt>
              {/* null = "확인 필요"(028) — 원칙 4: 정보 없음은 0점도 탈락도 아니다 */}
              <dd>{c.pilotAvailable === null ? '확인 필요' : pilotLabel(c.pilotAvailable)}</dd>

              <dt>수용 규모</dt>
              <dd>{c.capacityNote ?? <span className="muted">확인 필요</span>}</dd>

              <dt>착수까지</dt>
              <dd>
                {c.leadTimeDays !== null ? `약 ${c.leadTimeDays}일` : <span className="muted">확인 필요</span>}
              </dd>

              <dt>성과</dt>
              <dd>{c.impactNote ?? <span className="muted">확인 필요</span>}</dd>
            </dl>

            {c.tags && c.tags.length > 0 ? (
              <div className="tags" style={{ marginTop: 20 }}>
                {c.tags.map((t) => (
                  <span key={t} className="tag">
                    #{t}
                  </span>
                ))}
              </div>
            ) : null}
          </article>
        ) : null}
      </QueryState>
    </>
  )
}
